/**
 * Created by Ning Jiang on 4/1/2016.
 */

import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {BookOfShadowsSoundConfig} from "../BookOfShadowsSoundConfig";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {IGambleSoundConfig} from "@nolimitcity/slot-gamble/bin/NoLimitGamblePlugin";
import {GambleEvents} from "@nolimitcity/slot-gamble/bin/config/GambleEvents";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BookOfShadowsParsedInitData} from "../server/data/BookOfShadowsParsedInitData";

export class SoundManager {
    private static currentLoop: string = "";
    private _mode: string = "";
    private _nextMode: string = "";

    constructor() {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));

        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, () => {
            if (this._mode === BookOfShadowsGameMode.FREESPIN && this._nextMode === BookOfShadowsGameMode.FREESPIN) {
                SoundManager.playAmbientLoop(BookOfShadowsSoundConfig.instance.BONUS_GAME_AMBIANCE);
            } else {
                SoundManager.playAmbientLoop(BookOfShadowsSoundConfig.instance.MAIN_GAME_AMBIANCE_1);
            }
        });

        NolimitApplication.events.on(GambleEvents.GAMBLE_PLAY_EFFECT_SOUND, (data: IGambleSoundConfig) => this.playGambleEffectSound(data));
        NolimitApplication.events.on(GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, (data: IGambleSoundConfig) => this.playGambleKeypadSound(data));
        NolimitApplication.events.on(GambleEvents.GAMBLE_STOP_EFFECT_SOUND, (data: IGambleSoundConfig) => this.stopGambleEffectSound(data));
    }

    private static get soundPlayer(): INolimitAudioPlayer {
        return <INolimitAudioPlayer>SlotGame.sound;
    }

    // --- internal methods ---
    public static playAmbientLoop(name: string, duration?: number, volume?: number): void {
        this.currentLoop = name;
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume:volume
        });
    }

    // Please NOTE the duration is in millisecond!
    public static fadeAmbienceLoop(volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: duration,
            to: volume
        });
    }

    public static playSoundEffect(name: string, repeat?: number, volume?: number): void {
        this.soundPlayer.playTrack({
            name: name,
            repeat: repeat,
            volume: volume,
        })
    }

    public static stopSoundEffect(name: string, fadeOutDuration?: number): void {
        this.soundPlayer.stopTrack(name);
    }

    public static playKeypadSound(name: string): void {
        this.soundPlayer.playKeypadSound(name);
    }

    public static onFadeSoundEffect(name: string, volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: name,
            duration: duration,
            to: volume,
        });
    }

/*    public static reset(): void {
        this.soundPlayer.changeLoop({
            name: BookOfShadowsSoundConfig.instance.MAIN_GAME_AMBIANCE_1,
            fadeDurr: 300
        })
    }*/

    // --- game sound methods ---

    public static playReelLanding(reelId: number): void {
        Logger.logDev("sound playReelLanding", reelId);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.REELS_DEFAULT_BOUNCE[0]);
    }

    public static playScatterLanding(count: number): void {
        Logger.logDev("sound playScatterLanding", count);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.SCATTER_LANDING[count]);
    }

    public static playAnticipation(): void {
        Logger.logDev("sound playAnticipation");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.ANTICIPATION);
    }

    public static playAnticipationFail(): void {
        Logger.logDev("sound playAnticipationFail");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.ANTICIPATION_FAIL);
    }

    public static playWin(name: string): void {
        Logger.logDev("sound playWin ", name);
        SoundManager.playSoundEffect(name);
    }

    public static playWinCountUp(): void {
        Logger.logDev("sound playWinCountUp");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.WIN_COUNT_UP);
    }

    public static playWinCountUpEnd(): void {
        Logger.logDev("sound playWinCountUpEnd");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.WIN_COUNT_UP_END);
    }

    public static playBigWinInitialWin(): void {
        Logger.logDev("sound playBigWinInitialWin");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_INITIAL_WIN);
    }

    public static playBigWinStart(): void {
        Logger.logDev("sound playBigWinStart");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_START);
    }

    public static playBigWinLoop(): void {
        Logger.logDev("sound playBigWinLoop");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_LOOP);
    }

    public static playBigWinLevel(i: number): void {
        Logger.logDev("sound playBigWinLevel", i);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_LEVEL + i);
    }

    public static playBigWinCountUp(): void {
        Logger.logDev("sound playBigWinCountUp");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.WIN_COUNT_UP, Infinity);
    }

    public static playBigWinEnd(): void {
        Logger.logDev("sound playBigWinEnd");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_END);
    }

    public static playShadowRowChangeTo(i: number): void {
        Logger.logDev("sound playShadowRowChangeTo", i);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.SHADOW_ROWS_CHANGE_TO[i]);
    }

    public static playReelLock(): void {
        Logger.logDev("sound playReelLock");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.REEL_LOCK);
    }

    public static playReelUnLock(): void {
        Logger.logDev("sound playReelUnLock");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.REEL_UNLOCK);
    }

    public static playFSStart(): void {
        Logger.logDev("sound playFSStart");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_START);
    }

    public static playFSChooseSymbol(): void {
        Logger.logDev("sound playFSChooseSymbol");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_CHOOSE_SYMBOL_LOOP, Infinity);
    }

    public static playFSSpecialSymbolLanding(id: number): void {
        Logger.logDev("sound playFSSpecialSymbolLanding reel", id);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_SPECIAL_SYMBOL_LANDING);
    }

    public static playFSBetlineHighlight(): void {
        Logger.logDev("sound playFSBetlineHighlight",);
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_BETLINE_HIGHLIGHT);
    }

    public static playFSNewSymbols(): void {
        Logger.logDev("sound playFSNewSymbols");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_NEW_SYMBOLS);
    }

    public static playFSLine(i: number): void {
        Logger.logDev("sound playFSLine");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_LINE + i);
    }

    public static playFSReTrigger(): void {
        Logger.logDev("sound playFSReTrigger");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_RE_TRIGGER);
    }

    public static playFSEnd(): void {
        Logger.logDev("sound playFSEnd");
        SoundManager.playSoundEffect(BookOfShadowsSoundConfig.instance.FS_END);
    }

    // --- gamble sound methods ---
    private playGambleEffectSound(config: IGambleSoundConfig): void {
        let repeat: number = 0;
        if (config.loop) {
            repeat = Infinity;

            SoundManager.fadeAmbienceLoop(0, 100);
        }
        SoundManager.playSoundEffect(config.name, repeat);

    }

    private playGambleKeypadSound(config: IGambleSoundConfig): void {
        SoundManager.playKeypadSound(config.name);
    }

    private stopGambleEffectSound(config: IGambleSoundConfig): void {
        if (config.loop) {
            SoundManager.fadeAmbienceLoop(1, 100);
        }

        SoundManager.stopSoundEffect(config.name, config.fadeOutDuration);
    }

    /**
     * @description When init data is parsed.
     * @param data
     */
    private onInitDataParsed(data: BookOfShadowsParsedInitData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;
    }
}